import camelCase from 'lodash/camelCase';
import deburr from 'lodash/deburr';
import upperFirst from 'lodash/upperFirst';

export type ParsedText = {
  text: string;
  bold?: boolean;
  sup?: boolean;
  italic?: boolean;
};

const accentsMap = {
  A: 'Á|À|Ã|Â|Ä',
  a: 'á|à|ã|â|ä',
  E: 'É|È|Ê|Ë',
  e: 'é|è|ê|ë',
  I: 'Í|Ì|Î|Ï',
  i: 'í|ì|î|ï',
  O: 'Ó|Ò|Ô|Õ|Ö',
  o: 'ó|ò|ô|õ|ö',
  U: 'Ú|Ù|Û|Ü',
  u: 'ú|ù|û|ü',
  C: 'Ç',
  c: 'ç',
  N: 'Ñ',
  n: 'ñ',
};

const universalTextParser = ({
  parsedTextItems,
  symbol,
  symbolName,
}: {
  parsedTextItems: ParsedText[];
  symbol: string;
  symbolName: keyof ParsedText;
}): { parsedTextItems: ParsedText[]; isSymbolCurrentlyApplied: boolean } =>
  parsedTextItems.reduce<{ parsedTextItems: ParsedText[]; isSymbolCurrentlyApplied: boolean }>(
    (acc, { text, ...rest }) => {
      let currentText = text;
      const result = { ...acc };

      while (currentText.length > 0) {
        const symbolIndex = currentText.indexOf(symbol);
        const isSymbolEscaped = symbolIndex > 0 && currentText[symbolIndex - 1] === '\\';
        const isTextContainingSymbol = symbolIndex > 0;
        const isTextNotContainingSymbol = symbolIndex === -1;

        if (isTextNotContainingSymbol) {
          result.parsedTextItems.push({
            text: currentText.replace(`\\${symbol}`, ''),
            [symbolName]: result.isSymbolCurrentlyApplied,
            ...rest,
          });
          break;
        }

        if (isSymbolEscaped) {
          result.parsedTextItems.push({
            text: currentText.substring(0, symbolIndex - 1) + symbol,
            [symbolName]: result.isSymbolCurrentlyApplied,
            ...rest,
          });
        } else if (isTextContainingSymbol) {
          result.parsedTextItems.push({
            text: currentText.substring(0, symbolIndex),
            [symbolName]: result.isSymbolCurrentlyApplied,
            ...rest,
          });
        }

        result.isSymbolCurrentlyApplied = isSymbolEscaped
          ? result.isSymbolCurrentlyApplied
          : !result.isSymbolCurrentlyApplied;
        currentText = currentText.substring(symbolIndex + symbol.length);
      }

      return result;
    },
    { parsedTextItems: [], isSymbolCurrentlyApplied: false },
  );

const SYMBOLS_TO_PARSE: { symbol: string; symbolName: keyof ParsedText }[] = [
  { symbol: '**', symbolName: 'bold' },
  { symbol: '^', symbolName: 'sup' },
  { symbol: '__', symbolName: 'italic' },
];

export const parseText = (text: string) => {
  let parsedTextItems: ParsedText[] = [{ text }];

  SYMBOLS_TO_PARSE.forEach(({ symbol, symbolName }) => {
    const result = universalTextParser({
      parsedTextItems,
      symbol,
      symbolName,
    });
    parsedTextItems = result.parsedTextItems;
  });

  return parsedTextItems;
};

export const isStringEmpty = (stringToTest?: string | null) => !stringToTest || stringToTest.trim() === '';

// https://stackoverflow.com/a/44078785
export const guid = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

export const toPascalCase = (string: string) => upperFirst(camelCase(string));

export const toSnakeCase = (string: string) => string.trim().replace(/ /g, '_').toLowerCase();

export const removeTrailingSlash = (value: string | undefined) => (value ? value.replace(/\/$/, '') : '');

export const removeAccents = (string: string) =>
  [...(Object.keys(accentsMap) as Array<keyof typeof accentsMap>)].reduce(
    (acc: string, key) => acc.replace(new RegExp(accentsMap[key], 'g'), key),
    string,
  );

export const removeSpaces = (s: string): string => s.replace(/\s/g, '');

export const sanitizeString = (string: string): string => {
  if (!string) return '';

  return deburr(string.trim())
    .toLowerCase()
    .replace(/^(mademoiselle|monsieur|madame|mlle|mrs|mme|mr|m)\b\.?\s*/, '')
    .replace(/[-–—]/g, ' ')
    .replace(/[^a-z\s''‛]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const isStringContainedIn = (stringToSanitize: string, ...candidates: string[]): boolean => {
  const sanitizedString = sanitizeString(stringToSanitize);
  if (!sanitizedString) return false;

  const splitString = sanitizedString.split(' ');

  return candidates.some((candidate) => {
    if (!candidate) return false;
    const sanitizedCandidate = sanitizeString(candidate);
    const splitCandidate = sanitizedCandidate.split(' ');
    return splitCandidate.every((word) => splitString.includes(word));
  });
};

export const uppercaseLastTwo = (str: string): string =>
  str.length < 2 ? str.toUpperCase() : str.slice(0, -2) + str.slice(-2).toUpperCase();

// lodash's capitalize is setting the first letter to uppercase and forcing all others to lowercase.
// This util will only modify the first letter and not modify the others.
// This is important for some locales.
export const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const stringify = (input: any) => String(input);

export const parameterize = (string: string, options: { separator?: string; preserveCase?: boolean } = {}): string => {
  const { separator = '-', preserveCase = false } = options;

  if (!string) return '';

  let result = string;

  // Escape separator for regex usage
  const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Convert to lowercase unless preserveCase is true
  if (!preserveCase) {
    result = result.toLowerCase();
  }

  // Normalize unicode characters (e.g., é -> e, ñ -> n)
  result = result.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Replace spaces and special characters with the separator
  result = result.replace(/[^a-zA-Z0-9]/g, separator);

  // Collapse multiple separators into single ones
  result = result.replace(new RegExp(`${escapedSeparator}+`, 'g'), separator);

  // Trim separators from beginning and end
  result = result.replace(new RegExp(`^${escapedSeparator}+|${escapedSeparator}+$`, 'g'), '');

  return result;
};
